
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "button-touch-target inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 will-change-transform",
  {
    variants: {
      variant: {
        default: "bg-primary hover:bg-primary-hover hover:shadow-elevation-2 hover:scale-[1.02] active:scale-[0.98]",
        destructive:
          "bg-destructive hover:bg-destructive/90 hover:shadow-elevation-2 hover:scale-[1.02] active:scale-[0.98]",
        outline:
          "border border-input bg-background hover:bg-accent hover:border-border-hover hover:shadow-elevation-1 hover:scale-[1.02] active:scale-[0.98]",
        secondary:
          "bg-secondary hover:bg-secondary-hover hover:shadow-elevation-1 hover:scale-[1.02] active:scale-[0.98]",
        ghost: "hover:bg-accent hover:scale-[1.02] active:scale-[0.98]",
        link: "underline-offset-4 hover:underline",
        glass: "bg-surface-glass border border-border/30 hover:bg-surface-glass hover:border-border-hover/50 hover:shadow-elevation-2 hover:scale-[1.02] active:scale-[0.98] backdrop-blur-sm",
        gradient: "bg-gradient-to-r from-primary to-primary-glow hover:from-primary-hover hover:to-primary hover:shadow-glow hover:scale-[1.02] active:scale-[0.98]",
        success: "bg-success hover:bg-success/90 hover:shadow-elevation-2 hover:scale-[1.02] active:scale-[0.98]",
        warning: "bg-warning hover:bg-warning/90 hover:shadow-elevation-2 hover:scale-[1.02] active:scale-[0.98]",
        blue: "bg-blue-600 border-2 border-blue-600 hover:bg-gradient-to-r hover:from-primary hover:to-blue-600 hover:shadow-elevation-2 hover:scale-[1.02] active:scale-[0.98]",
        features: "bg-emerald-700 text-white hover:bg-emerald-800 hover:shadow-elevation-2 hover:scale-[1.02] active:scale-[0.98]",
        capabilities: "bg-purple-600 text-white hover:bg-purple-700 hover:shadow-elevation-2 hover:scale-[1.02] active:scale-[0.98]",
        security: "bg-red-600 text-white hover:bg-red-700 hover:shadow-elevation-2 hover:scale-[1.02] active:scale-[0.98]",
      },
      size: {
        default: "h-11 px-grid-4 py-grid-2 text-size-sm",
        sm: "h-10 px-grid-3 py-grid-1 text-size-xs rounded-sm",
        lg: "h-12 px-grid-8 py-grid-3 text-size-base rounded-lg",
        icon: "h-11 w-11",
        xl: "h-12 px-grid-10 py-grid-4 text-size-lg rounded-xl",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const paletteTextColor = /^(?:slate|gray|zinc|neutral|stone|red|orange|amber|yellow|lime|green|emerald|teal|cyan|sky|blue|indigo|violet|purple|fuchsia|pink|rose)-\d{2,3}(?:\/(?:\d{1,3}|\[[^\]]+\]))?$/
const semanticTextColor = /^(?:inherit|current|transparent|black|white|foreground|background|border|input|ring|(?:primary|secondary|destructive|success|warning|muted|accent|popover|card|surface)(?:-[a-z-]+)?)(?:\/(?:\d{1,3}|\[[^\]]+\]))?$/
const arbitraryTextColor = /^\[(?:color:|#|(?:rgb|hsl|hwb|lab|lch|oklab|oklch|color|var)\().+\]$/

// Typography utilities such as text-sm must not suppress a button's foreground.
const hasCustomTextColor = (className?: string) => {
  if (!className) return false;

  return className.split(/\s+/).some((token) => {
    if (token.includes('btn-text-')) return true

    const marker = token.lastIndexOf('text-')
    if (marker === -1) return false

    const value = token.slice(marker + 'text-'.length)
    return paletteTextColor.test(value)
      || semanticTextColor.test(value)
      || arbitraryTextColor.test(value)
  })
}

// Helper function to get default text color for variants
const getDefaultTextColor = (variant?: string) => {
  switch (variant) {
    case "default":
    case "destructive":
    case "success":
    case "warning":
    case "gradient":
    case "blue":
    case "features":
    case "capabilities":
    case "security":
      return "text-white hover:text-white";
    case "outline":
      return "text-foreground hover:text-accent-foreground";
    case "secondary":
      return "text-secondary-foreground hover:text-secondary-foreground";
    case "ghost":
      return "text-foreground hover:text-accent-foreground";
    case "link":
      return "text-primary hover:text-primary-hover";
    case "glass":
      return "text-foreground hover:text-foreground";
    default:
      return "text-primary-foreground hover:text-primary-foreground";
  }
}

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    
    // Determine if we should apply default text color
    const shouldApplyDefaultTextColor = !hasCustomTextColor(className);
    const defaultTextColor = shouldApplyDefaultTextColor ? getDefaultTextColor(variant) : "";
    
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size }), 
          defaultTextColor,
          className
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
